import React, { useEffect, useRef, useState } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

// Assuming this contains the CSS you provided
import { setPopupVisibility } from '../../store/serviceCalendarSlice';
import './DraggablePopup.css';

// --- Helper Function for Throttling ---
const throttle = (func, limit) => {
    let inThrottle;
    return function () {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
};
// --- End Helper Function ---

const DraggablePopup = ({ children, title, width = '160px', offsetIndex = 0 }) => {
    const dispatch = useDispatch();

    const isService = title === 'Service Types';

    const popupRef = useRef(null);
    const headerRef = useRef(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [hasBeenDragged, setHasBeenDragged] = useState(false); // <-- Track drag state

    // Function to calculate position based on viewport and props
    // Moved outside useEffect so it can be reused by resize handler
    const calculatePosition = () => {
        const elmnt = popupRef.current;
        if (!elmnt) return { x: 0, y: 0 }; // Should ideally not happen

        const viewportWidth = window.innerWidth;
        // Try to get computed width for accuracy, fallback to prop, then default
        const computedWidthStyle = window.getComputedStyle(elmnt).width;
        const popupWidth = parseInt(computedWidthStyle, 10) || parseInt(width, 10) || 160;

        const offsetX = 20 + offsetIndex; // Adjusted stagger slightly
        const offsetY = 50 + offsetIndex * 346; // Adjusted stagger slightly

        // Ensure position is not negative (e.g., if viewport is too small)
        const x = Math.max(0, viewportWidth - popupWidth - offsetX);
        const y = Math.max(0, offsetY);

        return { x, y };
    };

    // Effect for setting initial position AND handling drag logic
    useEffect(() => {
        const elmnt = popupRef.current;
        if (!elmnt) return;

        // Calculate and set initial position
        const initialPos = calculatePosition();
        setPosition(initialPos);
        elmnt.style.left = `${initialPos.x}px`;
        elmnt.style.top = `${initialPos.y}px`;
        setHasBeenDragged(false); // Reset drag status if props change

        // --- Drag Logic ---
        let pos1 = 0,
            pos2 = 0,
            pos3 = 0,
            pos4 = 0;
        let dragJustStarted = false;

        const dragMouseDown = (e) => {
            e = e || window.event;
            e.preventDefault(); // Prevent text selection, etc.
            pos3 = e.clientX;
            pos4 = e.clientY;
            dragJustStarted = true; // Flag to set hasBeenDragged on first move
            document.onmouseup = closeDragElement;
            document.onmousemove = elementDrag;
        };

        const elementDrag = (e) => {
            e = e || window.event;
            e.preventDefault();

            // Set hasBeenDragged on the very first mouse move after mousedown
            if (dragJustStarted) {
                setHasBeenDragged(true);
                dragJustStarted = false;
            }

            pos1 = pos3 - e.clientX;
            pos2 = pos4 - e.clientY;
            pos3 = e.clientX;
            pos4 = e.clientY;

            const newTop = elmnt.offsetTop - pos2;
            const newLeft = elmnt.offsetLeft - pos1;

            elmnt.style.top = newTop + 'px';
            elmnt.style.left = newLeft + 'px';
            setPosition({ x: newLeft, y: newTop }); // Keep state in sync
        };

        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
        };

        // Attach drag handler to header preferentially
        const header = headerRef.current;
        if (header) {
            header.onmousedown = dragMouseDown;
        } else {
            // Fallback: drag the whole popup if no header ref
            elmnt.onmousedown = dragMouseDown;
        }

        // Cleanup drag handlers
        return () => {
            if (header) {
                header.onmousedown = null;
            } else if (elmnt) {
                // Check if elmnt still exists
                elmnt.onmousedown = null;
            }
            // Important: Remove document listeners on cleanup too
            document.onmouseup = null;
            document.onmousemove = null;
        };
        // Dependencies: Recalculate initial position if width/offsetIndex change
    }, [width, offsetIndex]);

    // Effect for handling window resize
    useEffect(() => {
        const handleResize = () => {
            const elmnt = popupRef.current;
            // Only reposition automatically if it exists AND hasn't been dragged
            if (elmnt && !hasBeenDragged) {
                const newPos = calculatePosition(); // Use the same calculation logic
                setPosition(newPos);
                elmnt.style.left = `${newPos.x}px`;
                elmnt.style.top = `${newPos.y}px`;
            }
        };

        // Throttle the resize handler (100ms delay)
        const throttledHandleResize = throttle(handleResize, 100);

        window.addEventListener('resize', throttledHandleResize);

        // Cleanup: remove listener when component unmounts or dependencies change
        return () => {
            window.removeEventListener('resize', throttledHandleResize);
        };
        // Dependencies: Re-attach listener if drag status or calculation params change
    }, [hasBeenDragged, width, offsetIndex]);

    const handleClose = () => {
        if (isService) {
            dispatch(setPopupVisibility({ popupName: 'service', isVisible: false }));
        } else {
            dispatch(
                setPopupVisibility({
                    popupName: 'status',
                    isVisible: false
                })
            );
        }
    };

    return (
        <div
            ref={popupRef}
            className="draggable-popup"
            // Width is now controlled by the dynamic position calculation
            // Height is set to fit-content in CSS
            style={{ width /* You can still pass width if needed for calculation */ }}>
            <div ref={headerRef} className="draggable-popup-header">
                <div className="draggable-popup-title">{title}</div>
                <CloseOutlined onClick={handleClose} className="hover:bg-gray-500" />
            </div>
            <div className="draggable-popup-content">{children}</div>
        </div>
    );
};

DraggablePopup.propTypes = {
    children: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    width: PropTypes.string, // Still useful for initial calculation fallback
    offsetIndex: PropTypes.number
};

export default DraggablePopup;
